import React, { useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Flex,
  Badge,
  Divider,
  useColorModeValue,
  UnorderedList,
  ListItem,
  Code,
} from '@chakra-ui/react';
import { MessageContext } from '../types/dashboard';

interface MessageContextModalProps {
  isOpen: boolean;
  onClose: () => void;
  messageContext: MessageContext[];
  isLoading: boolean;
}

const MessageContextModal: React.FC<MessageContextModalProps> = ({
  isOpen,
  onClose,
  messageContext,
  isLoading,
}) => {
  const userBgColor = useColorModeValue('gray.100', 'gray.700');
  const assistantBgColor = useColorModeValue('blue.50', 'blue.900');
  const highlightColor = useColorModeValue('yellow.100', 'yellow.800');
  
  // Calculate font size based on message count
  const fontSize = useMemo(() => {
    if (messageContext.length > 8) return 'sm';
    if (messageContext.length > 4) return 'md';
    return 'md';
  }, [messageContext.length]);

  // Format message content to handle different types of content
  const formatContent = (content: string) => {
    // For ticket IDs, add special formatting
    const formattedContent = content.replace(
      /\*\*Ticket ID\*\*: (\d+)/g, 
      '<strong>Ticket ID:</strong> <code>$1</code>'
    ).replace(
      /\*\*(.*?)\*\*/g, 
      '<strong>$1</strong>'
    );
    
    // Check if it's a list-like content (numbered items)
    if (/^\d+\.\s+/.test(content)) {
      return (
        <Box pl={4} className="ticket-list">
          {content.split('\n').map((line, i) => {
            // Handle ticket list items
            const ticketMatch = line.match(/^(\d+)\.\s+(.*)/);
            if (ticketMatch) {
              return (
                <Box key={i} mb={2} fontWeight="500">
                  <Text as="span" fontWeight="bold" color="blue.600">{`${ticketMatch[1]}. `}</Text>
                  <Text 
                    as="span" 
                    dangerouslySetInnerHTML={{ 
                      __html: ticketMatch[2]
                        .replace(/\*\*Ticket ID\*\*: (\d+)/g, '<strong>Ticket ID:</strong> <code>$1</code>')
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    }} 
                  />
                </Box>
              );
            }
            
            // If it's not empty, render as regular text
            if (line.trim()) {
              return <Text key={i} mb={1}>{line}</Text>;
            }
            
            return null;
          })}
        </Box>
      );
    }
    
    // Check for multiline structured content like "Here are the tickets associated with..."
    if (content.includes("Here are the tickets") && content.includes("\n")) {
      const [intro, ...ticketDetails] = content.split("\n").filter(line => line.trim());
      
      return (
        <Box>
          <Text mb={3}>{intro}</Text>
          <Box pl={4}>
            {ticketDetails.map((line, i) => {
              if (!line.trim()) return null;
              
              const formattedLine = line
                .replace(/\*\*Ticket ID\*\*: (\d+)/g, '<strong>Ticket ID:</strong> <code>$1</code>')
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                
              return (
                <Text 
                  key={i} 
                  mb={2} 
                  dangerouslySetInnerHTML={{ __html: formattedLine }}
                />
              );
            })}
          </Box>
        </Box>
      );
    }
    
    // Handle normal text content
    return (
      <Box>
        <Text 
          whiteSpace="pre-wrap" 
          dangerouslySetInnerHTML={{ __html: formattedContent }} 
        />
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW={{ base: "90%", md: "800px" }}>
        <ModalHeader>Conversation Context</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Text>Loading conversation context...</Text>
          ) : messageContext.length === 0 ? (
            <Text>No conversation context found.</Text>
          ) : (
            <Box>
              {messageContext.map((message) => (
                <Box 
                  key={message.id} 
                  mb={4} 
                  p={3} 
                  borderRadius="md"
                  bg={message.is_target_message 
                    ? highlightColor 
                    : message.role === 'user' 
                      ? userBgColor 
                      : assistantBgColor
                  }
                  borderWidth={message.is_target_message ? '1px' : '0'}
                  borderColor="orange.300"
                  fontSize={fontSize}
                  className="message-content"
                  boxShadow={message.is_target_message ? "md" : "sm"}
                >
                  <Flex justify="space-between" mb={2} align="center">
                    <Text fontWeight="bold">
                      {message.role === 'user' 
                        ? `${message.first_name || ''} ${message.last_name || ''}`.trim() || `User ${message.user_id}`
                        : 'Assistant'
                      }
                      {message.is_target_message && (
                        <Badge ml={2} colorScheme="orange">Feedback Message</Badge>
                      )}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {new Date(message.created_at).toLocaleString()}
                    </Text>
                  </Flex>
                  <Divider mb={2} />
                  <Box className="message-body">
                    {formatContent(message.content)}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MessageContextModal; 